import type { ComponentChildren } from "preact";
import { createContext } from "preact";
import {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "preact/hooks";
import type { PictureConfiguration } from "@brickme/project-core/src/model/picture.ts";
import roundAndConstrainImageZoomOffset from "@brickme/project-core/src/utils/round-and-constrain-image-zoom-offset.ts";
import type { GenericSize } from "@brickme/project-core/src/model/geom.ts";
import { isEqual } from "@brickme/project-core/src/utils/unknown.ts";
import { debounce } from "@brickme/project-core/src/utils/function.ts";

type PicturePatch = Partial<
	Omit<PictureConfiguration, "updatedAt" | "numberOfBricks">
>;

type PictureEditValue = {
	readonly picture: PictureConfiguration;
	readonly setPicture: (picture: PictureConfiguration) => void;
	readonly patchPicture: (patch: PicturePatch) => void;
	readonly patchPictureDebounced: (patch: PicturePatch) => void;
};

const pictureContext = createContext<PictureEditValue | undefined>(undefined);

type PictureStorage = {
	readonly setPicture: (
		picture:
			| PictureConfiguration
			| ((previous: PictureConfiguration) => PictureConfiguration),
	) => void;
	readonly getPicture: () => PictureConfiguration;
	readonly addEventListener: (event: "change", handler: () => void) => void;
	readonly removeEventListener: (event: "change", handler: () => void) => void;
};

type PictureEditProviderProps = {
	readonly sourceImageDimensions: GenericSize<number>;
	readonly children: ComponentChildren;
	readonly storage: PictureStorage;
};

function PictureEditProvider({
	sourceImageDimensions,
	children,
	storage,
}: PictureEditProviderProps) {
	// To allow value useMemo to re-run
	const [pictureVersion, setPictureVersion] = useState(0);
	useEffect(() => {
		function onStorageChange() {
			setPictureVersion((p) => p + 1);
		}
		storage.addEventListener("change", onStorageChange);
		return () => {
			storage.removeEventListener("change", onStorageChange);
		};
	}, [storage]);

	const patchPicture = useCallback(
		(patch: PicturePatch) => {
			storage.setPicture((p) => {
				const intendedPicture = {
					...p,
					...patch,
				};
				const intendedPictureWithBricks = {
					...intendedPicture,
					numberOfBricks: {
						width: p.basePlateSize * intendedPicture.numberOfBasePlates.width,
						height: p.basePlateSize * intendedPicture.numberOfBasePlates.height,
					},
				};

				// Make sure domain bounds are enforced
				const finalPicture = {
					...intendedPictureWithBricks,
					// If changing numberOfBasePlates or baseplateSize then this can become invalid
					imageZoomOffset: roundAndConstrainImageZoomOffset(
						sourceImageDimensions,
						intendedPictureWithBricks,
						intendedPictureWithBricks.imageZoomOffset,
					),
				};

				// No change, use previous
				if (isEqual(finalPicture, p)) {
					return p;
				}

				// If imageZoom, basePlateSize or numberOfBasePlates are changed, then clear pens.
				// No sane way to scale them.
				if (
					finalPicture.imageZoom !== p.imageZoom ||
					finalPicture.basePlateSize !== p.basePlateSize ||
					!isEqual(finalPicture.numberOfBasePlates, p.numberOfBasePlates)
				) {
					finalPicture.pen = [];
				}

				return finalPicture;
			});
		},
		[sourceImageDimensions, storage],
	);

	// Keep a ref to the latest patchPicture so the debounced wrapper below can be
	// created once (preserving its internal timer across renders) while always
	// invoking the current implementation.
	const patchPictureRef = useRef(patchPicture);
	patchPictureRef.current = patchPicture;

	// Created ONCE. Previously this lived in the value memo and was rebuilt on every
	// pictureVersion bump (i.e. every patch). A fresh leading-edge debounce fires
	// immediately on its next call, so the rebuild defeated debouncing entirely and
	// every slider drag tick ran a full re-brick. A stable instance actually coalesces.
	const patchPictureDebounced = useMemo(
		() =>
			// Really only care about preventing multiple operations per frame
			debounce((patch: PicturePatch) => patchPictureRef.current(patch), 10, {
				leading: true,
				trailing: true,
			}),
		[],
	);

	const value = useMemo(
		(): PictureEditValue => ({
			picture: storage.getPicture(),
			patchPicture,
			setPicture: storage.setPicture,
			patchPictureDebounced,
		}),
		[
			storage,
			patchPicture,
			patchPictureDebounced,
			// Allows re-evaluating value (storage.getPicture). Raises lint error
			pictureVersion,
		],
	);

	return (
		<pictureContext.Provider value={value}>{children}</pictureContext.Provider>
	);
}

function usePicture() {
	const context = useContext(pictureContext);
	if (context === undefined) {
		throw new Error("usePicture must be used within a PictureProvider");
	}
	return context;
}

export { usePicture, PictureEditProvider };
